/**
 * 统一字段配置 - 消除硬编码映射表
 * 所有字段相关配置集中管理，新增字段只需在此添加一项
 */

import type { IAutoCompleteType } from '@patsnap/synapse_common_interface'
import { IFieldGroup, IFieldGroupFieldsItem } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { I18nLang } from '@pharmsnap/shared/src/i18n'
import { DrugFinderExpertKey, DrugFinderFieldType } from '@pharmsnap/shared/src/utils'
import { getTextByLang } from '@pharmsnap/shared/src/utils/lang'

// 常量定义
export const FIELD_GROUP_MAX_COUNT = 25
export const ALL_TAG_ID = 'All'

export type ISimpleQueryFieldKey = Extract<
  DrugFinderExpertKey,
  'drugItems' | 'drugTypeItems' | 'targetItems' | 'atcItems' | 'modificationItems' | 'allFieldItems' | 'dealExistItems' | 'highestPhaseItems'
>

export type ISimpleQueryEntityFieldKey = Extract<
  ISimpleQueryFieldKey,
  'drugItems' | 'drugTypeItems' | 'targetItems' | 'atcItems' | 'modificationItems'
>

// 字段配置接口
export interface IFieldConfig {
  // 基础信息
  key: DrugFinderExpertKey // 字段key: 'drugItems'
  queryField: string // Query字段: 'DRUG_ID'

  // 显示信息
  displayName: {
    cn: string
    en: string
    jp?: string
  }

  // 类型信息
  valueType: DrugFinderFieldType
  searchStrategy: 'ID' | 'ID_ROLLUP'
  autocompleteType?: IAutoCompleteType

  // UI配置
  dataType: 'tags' | 'selectedValues' // 使用tags还是selectedValues
  showBrowse?: boolean
  showExample?: boolean
  icon?: string

  // 特殊处理标识
  isSpecial?: boolean // 是否需要特殊处理
  skipInQuery?: boolean // 是否跳过Query生成
}

/**
 * 完整的字段配置表
 */
export const FIELD_CONFIG_MAP: Record<DrugFinderExpertKey, IFieldConfig> = {
  // 药物相关
  drugItems: {
    key: 'drugItems',
    queryField: 'DRUG_ID',
    displayName: { cn: '药物', en: 'Drug', jp: '医薬品' },
    valueType: 'drug',
    searchStrategy: 'ID',
    autocompleteType: 'Drug',
    dataType: 'tags',
    showBrowse: false,
    showExample: true,
    icon: 'Drug',
  },

  drugTypeItems: {
    key: 'drugTypeItems',
    queryField: 'DRUG_TYPE',
    displayName: { cn: '药物类型', en: 'Drug Type', jp: 'モダリティ' },
    valueType: 'text',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'DrugType',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
  },

  // 靶点
  targetItems: {
    key: 'targetItems',
    queryField: 'TARGET_ID',
    displayName: { cn: '靶点', en: 'Target', jp: '標的' },
    valueType: 'target',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'Target',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
    icon: 'Target',
  },

  // 适应症
  indicationItems: {
    key: 'indicationItems',
    queryField: 'DISEASE_ID',
    displayName: { cn: '适应症', en: 'Indication', jp: '適応症' },
    valueType: 'disease',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'Disease',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
    icon: 'Disease',
  },

  indicationPhaseItems: {
    key: 'indicationPhaseItems',
    queryField: 'INDICATION_PHASE',
    displayName: { cn: '适应症状态', en: 'Indication Status', jp: '開発状況' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
    skipInQuery: true,
    isSpecial: true,
  },

  // 机构
  organizationItems: {
    key: 'organizationItems',
    queryField: 'ORGANIZATION_ID_ONEID',
    displayName: { cn: '机构', en: 'Organization', jp: '組織' },
    valueType: 'organization',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'Organization',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
    icon: 'Organization',
  },

  orgRoleItems: {
    key: 'orgRoleItems',
    queryField: 'DEV_ORG_TYPE_ONEID',
    displayName: { cn: '机构角色', en: 'Org Role', jp: '組織の役割' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
    skipInQuery: true,
    isSpecial: true,
  },

  // 作用机制
  actionItems: {
    key: 'actionItems',
    queryField: 'ACTION_ID',
    displayName: { cn: '作用方式', en: 'Action', jp: '作用様式' },
    valueType: 'text',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'Action',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
  },

  mechanismItems: {
    key: 'mechanismItems',
    queryField: 'MECHANISM_ACTION_ID',
    displayName: { cn: '作用机制', en: 'Mechanism', jp: '作用機序' },
    valueType: 'text',
    searchStrategy: 'ID',
    autocompleteType: 'Mechanism',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
  },

  // 里程碑
  milestoneItems: {
    key: 'milestoneItems',
    queryField: 'MILESTONE_ID',
    displayName: { cn: '里程碑', en: 'Milestone', jp: 'マイルストーン' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
    skipInQuery: true,
    isSpecial: true,
  },

  milestoneDate: {
    key: 'milestoneDate',
    queryField: 'MILESTONE_DATE',
    displayName: { cn: '里程碑日期', en: 'Milestone Date', jp: 'マイルストーン日付' },
    valueType: 'dateRange',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
    skipInQuery: true,
    isSpecial: true,
  },

  // 最高阶段
  highestPhaseItems: {
    key: 'highestPhaseItems',
    queryField: 'GLOBAL_HIGHEST_DEV_STATUS',
    displayName: { cn: '最高阶段', en: 'Highest Phase', jp: '最高開発段階' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  // 其他字段
  atcItems: {
    key: 'atcItems',
    queryField: 'ATC_CODE',
    displayName: { cn: 'ATC', en: 'ATC', jp: 'ATC' },
    valueType: 'atc',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'ATC',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
  },

  modificationItems: {
    key: 'modificationItems',
    queryField: 'MODIFICATION_ID',
    displayName: { cn: '化学修饰', en: 'Chemical Modification', jp: '化学修飾' },
    valueType: 'text',
    searchStrategy: 'ID_ROLLUP',
    autocompleteType: 'MODIFICATION_ID',
    dataType: 'tags',
    showBrowse: true,
    showExample: true,
  },

  allFieldItems: {
    key: 'allFieldItems',
    queryField: 'ALL_FIELDS',
    displayName: { cn: '关键词', en: 'Keyword', jp: 'キーワード' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'tags',
  },

  dealExistItems: {
    key: 'dealExistItems',
    queryField: 'EXIST_DEAL',
    displayName: { cn: '存在交易', en: 'Deal', jp: '提携の有無' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  // 组织属性
  organizationTypeItems: {
    key: 'organizationTypeItems',
    queryField: 'ORG_TYPE_ONEID',
    displayName: { cn: '机构类型', en: 'Organization Type', jp: '組織タイプ' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  employeeNumberItems: {
    key: 'employeeNumberItems',
    queryField: 'ORG_EMP_NUMBER_GENERAL_ONEID',
    displayName: { cn: '员工数量', en: 'Employee Number', jp: '従業員数' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  startupItems: {
    key: 'startupItems',
    queryField: 'DEV_STATUS_ORG_IS_STARTUPS',
    displayName: { cn: '初创公司', en: 'Startup', jp: 'スタートアップ' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  // 当前研发状态
  phaseItems: {
    key: 'phaseItems',
    queryField: 'DEV_STATUS',
    displayName: { cn: '研发状态（基于适应症）', en: 'Phase (By Indication)', jp: '開発段階(適応症別)' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  approvedCountryItems: {
    key: 'approvedCountryItems',
    queryField: 'COUNTRY_COLLAPSED',
    displayName: { cn: '国家/地区', en: 'Country/Location', jp: '国/地域' },
    valueType: 'text',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },

  dateItems: {
    key: 'dateItems',
    queryField: 'DEV_STATUS_TIME',
    displayName: { cn: '日期', en: 'Date', jp: '日付' },
    valueType: 'dateRange',
    searchStrategy: 'ID',
    dataType: 'selectedValues',
  },
}

/**
 * 辅助函数 - 获取字段配置
 */
export const getFieldConfig = (fieldKey: DrugFinderExpertKey): IFieldConfig | undefined => {
  return FIELD_CONFIG_MAP[fieldKey]
}

export const getFieldQueryField = (fieldKey: DrugFinderExpertKey): string => {
  return FIELD_CONFIG_MAP[fieldKey]?.queryField || fieldKey.toUpperCase()
}

export const getFieldDisplayName = (fieldKey: DrugFinderExpertKey, locale: I18nLang): string => {
  const displayName = FIELD_CONFIG_MAP[fieldKey]?.displayName
  if (!displayName) return fieldKey
  return getTextByLang(displayName, locale) || fieldKey
}

export const getFieldValueType = (fieldKey: DrugFinderExpertKey) => {
  return FIELD_CONFIG_MAP[fieldKey]?.valueType || 'text'
}

export const getFieldSearchStrategy = (fieldKey: DrugFinderExpertKey) => {
  return FIELD_CONFIG_MAP[fieldKey]?.searchStrategy || 'ID'
}

/**
 * 特殊字段处理配置
 */
export const FIELD_SPECIAL_HANDLERS = {
  // 适应症阶段字段映射
  indicationPhase: {
    All: ['RESEARCH_DISEASE', 'NON_RESEARCH_DISEASE'],
    Active: ['RESEARCH_DISEASE'],
    Inactive: ['NON_RESEARCH_DISEASE'],
  },

  // 机构角色字段映射
  orgRole: {
    All: ['DEV_ORG_ID_ONEID', 'NON_DEV_ORG_ID_ONEID', 'ORIGINAL_DEV_ORG_ID_ONEID', 'LICENSE_ORG_ID_ONEID'],
    Active: ['DEV_ORG_ID_ONEID'],
    Inactive: ['NON_DEV_ORG_ID_ONEID'],
    Originator: ['ORIGINAL_DEV_ORG_ID_ONEID'],
    EquityInstitutions: ['LICENSE_ORG_ID_ONEID'],
  },
}

// 工具函数：安全访问字段，带默认值
export const getGroupField = (group: IFieldGroup, fieldKey: DrugFinderExpertKey): IFieldGroupFieldsItem => {
  return group.fields[fieldKey] || { fields: [], tags: [], selectedValues: [] }
}

// 工具函数：检查字段是否存在且有有效值
export const hasFieldValue = (group: IFieldGroup, fieldKey: DrugFinderExpertKey): boolean => {
  const field = group.fields[fieldKey]
  return !!(field && ((field.tags && field.tags.length > 0) || (field.selectedValues && field.selectedValues.length > 0)))
}

// 工具函数：安全设置字段值
export const setGroupField = (group: IFieldGroup, fieldKey: DrugFinderExpertKey, value: Partial<IFieldGroupFieldsItem>): void => {
  if (!group.fields[fieldKey]) {
    group.fields[fieldKey] = { fields: [], tags: [], selectedValues: [] }
  }
  const existingField = group.fields[fieldKey]
  if (existingField) {
    Object.assign(existingField, value)
  }
}
